import { createPagination } from "./utilities/paginationBuilder";
import { getFilterClause, createPartialURL } from "./utilities/urlBuilder";
import { getDeviceSettingStatus } from "./utilities/StatusBuilder";
import { Deserializer } from "jsonapi-serializer";
import { DeviceEventAction, DeviceEventActionWrapper } from "../models/DeviceEventAction";
import { ListQuery } from "../models/ListQuery";
import { APIConnector } from "./api/APIConnector";
import { Name } from "../models/Name";
import { PaginationModel } from "../models/Pagination";
import { genericError } from "../models/GenericError";
import { EVENT_ACTION_KEYS } from "../constants/command-status";
import { Filter } from "../models/Filter";
import { INTERNAL_SERVER_ERROR, NO_CONTENT } from "../constants/response-status-codes";
import { processServerError, getConstraintViolation } from "./utilities/ServiceError";

const apiConnector = new APIConnector();

/**
 *
 * @param event
 * @param createEventAcion
 *
 * The newRecord object is build differently based on the deviceEventTypeId. When the selected event type is
 * ALL ALLOWED or ALL DENIED we use the devActionGroup endpoint otherwise we use devEventAction endpoint.
 *
 */
export const storeEventAction = async (event, createEventAcion = true) => {
    try {
        const newRecord = event.deviceEventTypeId === EVENT_ACTION_KEYS.ALL_ALLOWED_KEY || event.deviceEventTypeId === EVENT_ACTION_KEYS.ALL_DENIED_KEY || event.requestType === EVENT_ACTION_KEYS.UNASSIGN_KEY ?
            {
                data: {
                    id: 0,
                    type: "devActionGroup",
                    attributes: {
                        deviceImproxId: event.eventDeviceImproxId,
                        devActionId: event.eventActionId,
                        eventGroupName: event.deviceEventTypeId === EVENT_ACTION_KEYS.ALL_ALLOWED_KEY || event.deviceEventTypeId === EVENT_ACTION_KEYS.ALL_DENIED_KEY ? event.deviceEventTypeId : undefined,
                        eventTypeId: event.deviceEventTypeId !== EVENT_ACTION_KEYS.ALL_ALLOWED_KEY && event.deviceEventTypeId !== EVENT_ACTION_KEYS.ALL_DENIED_KEY ? event.deviceEventTypeId : undefined,
                        requestType: event.requestType
                    }
                }
            } :
            {
                data: {
                    id: 0,
                    type: "devEventAction",
                    attributes: {
                        eventDeviceImproxId: event.eventDeviceImproxId,
                        eventTypeId: event.deviceEventTypeId
                    },
                    relationships: {
                        devAction: {
                            data: {
                                type: "devAction",
                                id: event.eventActionId,
                            }
                        }
                    }
                }
            }

        const url = newRecord.data.type;
        let response: Response;

        await apiConnector.sendData(url, true, newRecord).then(data => {
            response = data;
        }).catch(error => {
            console.log('Save event action error: ', error);
        });

        return new Promise(async (resolve, reject) => {
            if (response) {
                if (createEventAcion) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                    }
                    new Deserializer().deserialize(content, (error) => {
                        if (error) {
                            return reject({ status: response.status, success: false, message: genericError });
                        }
                        return resolve({ success: true });
                    })
                    return;
                }
                if (response.status === NO_CONTENT) {
                    return resolve({ id: event.id, success: true })
                }

                const content = await response.json();
                return reject({ status: response.status, id: event.id, success: false, message: content.errors.map(error => getConstraintViolation(error.detail)) });
            }

            return reject({ status: INTERNAL_SERVER_ERROR, id: event.id, success: false, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getDeviceEventActions = async (itemId: string, eventTypeID: string, listParameters?: ListQuery): Promise<DeviceEventActionWrapper> => {
    const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
    let filterOutEventType = `${filter ? filter.replace('filter=', `filter=deviceEvents.deviceImprox.id==${itemId};deviceEvents.eventType.id==${eventTypeID};`) : `&filter=deviceEvents.deviceImprox.id==${itemId};deviceEvents.eventType.id==${eventTypeID}`}`;
    const devActionWithEventsFields = "fields[devActionWithEvents]=setting,deviceImprox";
    const terminalFields = "fields[terminal]=devName";
    const settingFields = "fields[setting]=trnName,settingType,hierarchyType";
    const settingTypeFields = "fields[settingType]=trnName"; 
    const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filterOutEventType);
    const url = `devActionWithEvents?include=setting.settingType,deviceImprox,deviceEvents.eventType,deviceEvents.deviceImprox${paginationString}&${devActionWithEventsFields}&${terminalFields}&${settingFields}&${settingTypeFields}`;
    
    return fetchDeviceEvenAction(url);
}

export const getDeviceEventActionIds = async (eventActionsIds?: number[], listParameters?: ListQuery): Promise<DeviceEventActionWrapper> => {
    const eventActionIdOptions = eventActionsIds?.map(eventActionId => { return { label: `${eventActionId}`, value: `${eventActionId}` } }) ?? [];
    const idFilter: Filter = {
        key: "id",
        label: "Id", //not displayed
        options: eventActionIdOptions,
        selected: eventActionIdOptions
    };

    const filter: string = getFilterClause([...listParameters?.filters ?? [], idFilter], listParameters?.searchTerm);
    const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
    const settingFields = "fields[setting]=settingType,trnName,hierarchyType";
    const deviceImproxFields = "fields[terminal]=devName";
    const settingTypeFields = "fields[settingType]=trnName";
    const devActionFields = "fields[devAction]=setting,deviceImprox";
    const include = "include=setting,deviceImprox,setting.settingType";
    const url = `devAction?${include}${paginationString}&${settingFields}&${deviceImproxFields}&${settingTypeFields}&${devActionFields}`;

    return fetchDeviceEvenAction(url);
}

const fetchDeviceEvenAction = async (url: string): Promise<DeviceEventActionWrapper> => {
    try {
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }
                const deviceEventAction: DeviceEventAction[] = data.map((deviceEventAction): DeviceEventAction => {
                    return {
                        id: deviceEventAction['id'],
                        name: deviceEventAction['setting']['trn-name'],
                        type: deviceEventAction['setting']['setting-type']['trn-name'],
                        status: getDeviceSettingStatus(deviceEventAction['setting']['hierarchy-type'] ? deviceEventAction['setting']['hierarchy-type'] : 0),
                        device: deviceEventAction['device-improx'] ? deviceEventAction['device-improx']['dev-name'] : ''
                    }
                })
                return resolve({ rows: deviceEventAction, pagination });
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getAllDeviceEventActions = async (listParameters?: ListQuery): Promise<DeviceEventActionWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const url = `devAction?include=setting,deviceImprox,setting.settingType${paginationString}`
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }
                const deviceAction = data.map((deviceAction) => {
                    return {
                        id: deviceAction['id'],
                        name: deviceAction['setting']['trn-name'],
                        type: deviceAction['setting']['setting-type']['trn-name'],
                        status: getDeviceSettingStatus(deviceAction['setting']['hierarchy-type'] ? deviceAction['setting']['hierarchy-type'] : 0),
                        device: deviceAction['device-improx']['dev-name']
                    }
                })

                return resolve({ rows: deviceAction, pagination });
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getDeviceEventActionsWithId = async (itemId: string, improxId) => {
    try {
        const url = `devEventAction?include=devAction,deviceEvent.deviceImprox,deviceEvent.eventType&filter=deviceEvent.deviceImprox.id==${improxId};deviceEvent.eventType.id==${itemId}`

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);


        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const deviceEventAction = data.map((deviceEventAction) => {
                    return {
                        id: deviceEventAction['id'],
                        devAction: deviceEventAction['dev-action']['id'],
                    }
                })

                return resolve({ rows: deviceEventAction, pagination });
            })
            return;
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const deleteDeviceEventAction = async (event): Promise<{ id: string, success: boolean }> => {
    try {
        const url = `devActionGroup/0`;
        const newRecord = {
            data: {
                id: 0,
                type: "devActionGroup",
                attributes: {
                    deviceImproxId: event.deviceId,
                    devActionId: event.eventActionId,
                    eventGroupName: event.deviceEventTypeId === EVENT_ACTION_KEYS.ALL_ALLOWED_KEY || event.deviceEventTypeId === EVENT_ACTION_KEYS.ALL_DENIED_KEY ? event.deviceEventTypeId : undefined,
                    eventTypeId: event.deviceEventTypeId !== EVENT_ACTION_KEYS.ALL_ALLOWED_KEY && event.deviceEventTypeId !== EVENT_ACTION_KEYS.ALL_DENIED_KEY ? event.deviceEventTypeId : undefined,
                }
            }
        }

        const response = await apiConnector.deleteData(url, newRecord);

        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: '0', success: true })
            }
            const json = await response.json();

            return reject({ status: response.status, id: '0', success: false, message: getConstraintViolation(json.errors[0].detail) });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getDeviceEventActionTypeNamesWithPagination = async (devicePosition: boolean, listParameters?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const deviceEventFields = 'fields[deviceEvent]=eventType';
        const eventTypeFields = 'fields[eventType]=trnName';
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const url = `deviceEvent?include=eventType${paginationString}&${deviceEventFields}&${eventTypeFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, data: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const deviceEventActionType: Name[] = data.map((deviceEventActionType) => {
                    return {
                        value: deviceEventActionType['event-type']['id'],
                        label: deviceEventActionType['event-type']['trn-name']
                    };
                });

                if (devicePosition) {
                    deviceEventActionType.unshift({ value: EVENT_ACTION_KEYS.ALL_DENIED_KEY, label: 'All Denied' })
                    deviceEventActionType.unshift({ value: EVENT_ACTION_KEYS.ALL_ALLOWED_KEY, label: 'All Allowed' })
                }

                return resolve({ rows: deviceEventActionType, pagination: newPagination });
            });
            return;
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

